import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import moment from 'moment';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import { CSVLink } from 'react-csv';
import { FaFileExport } from 'react-icons/fa';

import { getReservationsByAdmin } from './queries';
import { Dialog } from '../../../components';
import FilterFromToV2 from '../../../components/Filter/FilterFromToV2';
import styles from './styles';
import { colors } from '../../../constants';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'RESERVED', label: 'Reserved' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'EXPIRED', label: 'Expired' },
];

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: colors.parimary,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class TableReservations extends Component {
  state = {
    title: '',
    consumerName: '',
    merchantName: '',
    status: '',
    confirmedFromDate: '',
    confirmedToDate: '',
    minCashback: '',
    reservedFromDate: '',
    reservedToDate: '',
    closedFromDate: '',
    closedToDate: '',
    expiredFromDate: '',
    expiredToDate: '',
    page: 1,
    limit: 15,
    exportData: [],
    exporting: false,
    detailOpen: false,
    selectedDeal: null,
  };

  componentDidUpdate(prevProps, prevState) {
    const filterKeys = [
      'page', 'limit', 'title', 'consumerName', 'merchantName', 'status',
      'confirmedFromDate', 'confirmedToDate', 'minCashback',
      'reservedFromDate', 'reservedToDate',
      'closedFromDate', 'closedToDate',
      'expiredFromDate', 'expiredToDate',
    ];
    const changed = filterKeys.some((key) => prevState[key] !== this.state[key]);
    if (changed) {
      this.refetchData();
    }
  }

  buildFiltersInput = () => {
    const {
      title, consumerName, merchantName, status,
      confirmedFromDate, confirmedToDate, minCashback,
      reservedFromDate, reservedToDate,
      closedFromDate, closedToDate,
      expiredFromDate, expiredToDate,
    } = this.state;

    return {
      title: title || null,
      consumerName: consumerName || null,
      merchantName: merchantName || null,
      status: status || null,
      confirmedFromDate: confirmedFromDate || null,
      confirmedToDate: confirmedToDate || null,
      minCashback: minCashback !== '' ? parseFloat(minCashback) : null,
      reservedFromDate: reservedFromDate || null,
      reservedToDate: reservedToDate || null,
      closedFromDate: closedFromDate || null,
      closedToDate: closedToDate || null,
      expiredFromDate: expiredFromDate || null,
      expiredToDate: expiredToDate || null,
    };
  };

  refetchData = () => {
    const { refetch } = this.props;
    if (!refetch) return;
    const { page, limit } = this.state;
    refetch({
      input: {
        ...this.buildFiltersInput(),
        page: page || 1,
        limit: limit || 15,
      },
    });
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value, page: 1 });
  };

  handleChangePage = (event, page) => {
    this.setState({ page: page + 1 });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ limit: event.target.value, page: 1 });
  };

  parseDealDate = (value) => {
    if (!value) return null;
    const timestamp = Number(value);
    const date = !isNaN(timestamp) && timestamp > 0
      ? moment(timestamp)
      : moment(value);
    return date.isValid() ? date : null;
  };

  formatDate = (value) => {
    const date = this.parseDealDate(value);
    return date ? date.format('MM/DD/YYYY') : '-';
  };

  formatTime = (value) => {
    const date = this.parseDealDate(value);
    return date ? date.format('HH:mm') : '-';
  };

  formatDateTime = (value) => {
    const date = this.parseDealDate(value);
    return date ? date.format('MM/DD/YYYY HH:mm') : '-';
  };

  getTitle = (deal) => (deal.offer && deal.offer.title ? deal.offer.title : '-');

  getConsumerName = (deal) => (deal.consumer && deal.consumer.username ? deal.consumer.username : '-');

  getMerchantName = (deal) => (deal.merchant && deal.merchant.username ? deal.merchant.username : '-');

  getConfirmedDate = (deal) => deal.closedAt;

  formatRow = (deal) => ({
    Title: this.getTitle(deal),
    ConsumerName: this.getConsumerName(deal),
    MerchantName: this.getMerchantName(deal),
    Status: deal.state || '',
    'Confirmed Date': this.formatDate(this.getConfirmedDate(deal)),
    'Confirmed Time': this.formatTime(this.getConfirmedDate(deal)),
    'Cashback value': deal.reserveCoin != null ? deal.reserveCoin : '',
    'Date/Time Reserved': this.formatDateTime(deal.createdAt),
    'Date/Time Closed': this.formatDateTime(deal.closedAt),
    'Date/Time Expired': this.formatDateTime(deal.expiredAt),
  });

  prepareDataForCSV = (deals = []) => deals.map((deal) => this.formatRow(deal));

  onExportToCSV = () => {
    const { client } = this.props;
    const { limit } = this.state;
    if (!client) return;

    this.setState({ exporting: true }, async () => {
      try {
        const baseInput = {
          ...this.buildFiltersInput(),
          page: 1,
          limit: limit || 15,
        };

        const firstResult = await client.query({
          query: getReservationsByAdmin,
          variables: { input: baseInput },
          fetchPolicy: 'no-cache',
        });
        const firstPayload = firstResult && firstResult.data
          ? firstResult.data.getReservationsByAdmin
          : null;

        if (!firstPayload || !firstPayload.count_rows) {
          this.setState({ exporting: false, exportData: [] });
          return;
        }

        let allDeals = (firstPayload.deals || []).slice();
        const totalPage = firstPayload.total_page || 1;

        for (let currentPage = 2; currentPage <= totalPage; currentPage += 1) {
          const pageResult = await client.query({
            query: getReservationsByAdmin,
            variables: { input: { ...baseInput, page: currentPage } },
            fetchPolicy: 'no-cache',
          });
          const pagePayload = pageResult && pageResult.data
            ? pageResult.data.getReservationsByAdmin
            : null;
          if (pagePayload && pagePayload.deals && pagePayload.deals.length) {
            allDeals = allDeals.concat(pagePayload.deals);
          }
        }

        this.setState({
          exportData: this.prepareDataForCSV(allDeals),
          exporting: false,
        }, () => {
          if (this.csvLinkRef) {
            this.csvLinkRef.link.click();
          }
        });
      } catch (error) {
        this.setState({ exporting: false });
      }
    });
  };

  handleOpenDetail = (deal) => {
    this.setState({ detailOpen: true, selectedDeal: deal });
  };

  handleCloseDetail = () => {
    this.setState({ detailOpen: false, selectedDeal: null });
  };

  renderDetailContent = () => {
    const { selectedDeal } = this.state;
    if (!selectedDeal) return null;

    return (
      <div style={{ minWidth: 320 }}>
        <Typography variant="body2" gutterBottom>
          <strong>Title:</strong> {this.getTitle(selectedDeal)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Consumer:</strong> {this.getConsumerName(selectedDeal)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Merchant:</strong> {this.getMerchantName(selectedDeal)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Status:</strong> {selectedDeal.state || '-'}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Date/Time Reserved:</strong> {this.formatDateTime(selectedDeal.createdAt)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Date/Time Closed:</strong> {this.formatDateTime(selectedDeal.closedAt)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Date/Time Expired:</strong> {this.formatDateTime(selectedDeal.expiredAt)}
        </Typography>
      </div>
    );
  };

  renderDateRangeFilter = (label, fromName, toName) => {
    const fromValue = this.state[fromName];
    const toValue = this.state[toName];

    return (
      <FilterFromToV2
        label={label}
        fromField={(
          <TextField
            name={fromName}
            type="date"
            value={fromValue}
            onChange={this.handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />
        )}
        toField={(
          <TextField
            name={toName}
            type="date"
            value={toValue}
            onChange={this.handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />
        )}
        resetFn={() => this.setState({ [fromName]: '', [toName]: '', page: 1 })}
        smallButton
      />
    );
  };

  renderFilters = () => {
    const { classes } = this.props;
    const {
      title, consumerName, merchantName, status, minCashback,
    } = this.state;

    return (
      <div>
        <div className={classes.filterRow}>
          <FilterFromToV2
            label="Title"
            fromField={(
              <TextField
                name="title"
                value={title}
                onChange={this.handleFilterChange}
                placeholder="Title"
              />
            )}
            resetFn={() => this.setState({ title: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Consumer Name"
            fromField={(
              <TextField
                name="consumerName"
                value={consumerName}
                onChange={this.handleFilterChange}
                placeholder="Consumer"
              />
            )}
            resetFn={() => this.setState({ consumerName: '', page: 1 })}
            smallButton
          />
          <FilterFromToV2
            label="Merchant Name"
            fromField={(
              <TextField
                name="merchantName"
                value={merchantName}
                onChange={this.handleFilterChange}
                placeholder="Merchant"
              />
            )}
            resetFn={() => this.setState({ merchantName: '', page: 1 })}
            smallButton
          />
          <FormControl className={classes.statusSelect}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              name="status"
              value={status}
              onChange={this.handleFilterChange}
              displayEmpty
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FilterFromToV2
            label="Cashback greater than"
            fromField={(
              <TextField
                name="minCashback"
                type="number"
                value={minCashback}
                onChange={this.handleFilterChange}
                placeholder="Value"
              />
            )}
            resetFn={() => this.setState({ minCashback: '', page: 1 })}
            smallButton
          />
        </div>
        <div className={classes.filterRow}>
          {this.renderDateRangeFilter('Confirmed Date', 'confirmedFromDate', 'confirmedToDate')}
          {this.renderDateRangeFilter('Date Reserved', 'reservedFromDate', 'reservedToDate')}
          {this.renderDateRangeFilter('Date Closed', 'closedFromDate', 'closedToDate')}
          {this.renderDateRangeFilter('Date Expired', 'expiredFromDate', 'expiredToDate')}
        </div>
      </div>
    );
  };

  renderToolBar = () => {
    const { classes, reservationsData } = this.props;
    const { exportData, exporting } = this.state;
    const countRows = (reservationsData && reservationsData.count_rows) || 0;

    return (
      <div className={classes.searchWrapper}>
        <Button
          variant="contained"
          color="primary"
          className={classNames(classes.button, classes.exportButton)}
          onClick={this.onExportToCSV}
          disabled={exporting}
        >
          <Typography component="p" className={classes.text}>
            {exporting ? 'Exporting...' : 'Export'}
          </Typography>
          <FaFileExport color="#ffffff" size={16} />
        </Button>
        <CSVLink
          data={exportData}
          filename={`reservations-${moment().format('YYYY-MM-DD-HHmmss')}.csv`}
          style={{ display: 'none' }}
          ref={(r) => { this.csvLinkRef = r; }}
        >
          Export
        </CSVLink>
        <Typography component="p" style={{ fontSize: '14px' }}>
          Rows: {countRows}
        </Typography>
      </div>
    );
  };

  render() {
    const { classes, reservationsData, loading } = this.props;
    const { page, limit, detailOpen } = this.state;

    if (loading && !reservationsData) return null;

    const deals = (reservationsData && reservationsData.deals) || [];
    const countRows = (reservationsData && reservationsData.count_rows) || 0;

    return (
      <div>
        <Dialog
          title="Reservation Detail"
          listChildren={this.renderDetailContent()}
          open={detailOpen}
          onOk={this.handleCloseDetail}
        />
        {this.renderFilters()}
        {this.renderToolBar()}
        <Paper className={classes.root}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <CustomTableCell>Title</CustomTableCell>
                <CustomTableCell>Consumer Name</CustomTableCell>
                <CustomTableCell>Merchant Name</CustomTableCell>
                <CustomTableCell>Status</CustomTableCell>
                <CustomTableCell align="right">Confirmed Date</CustomTableCell>
                <CustomTableCell align="right">Confirmed Time</CustomTableCell>
                <CustomTableCell align="right">Cashback value</CustomTableCell>
                <CustomTableCell align="center">Detail</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal) => {
                const confirmedDate = this.getConfirmedDate(deal);
                return (
                  <TableRow className={classes.row} key={deal.id}>
                    <CustomTableCell component="th" scope="row">
                      {this.getTitle(deal)}
                    </CustomTableCell>
                    <CustomTableCell>{this.getConsumerName(deal)}</CustomTableCell>
                    <CustomTableCell>{this.getMerchantName(deal)}</CustomTableCell>
                    <CustomTableCell>{deal.state || '-'}</CustomTableCell>
                    <CustomTableCell align="right">
                      {this.formatDate(confirmedDate)}
                    </CustomTableCell>
                    <CustomTableCell align="right">
                      {this.formatTime(confirmedDate)}
                    </CustomTableCell>
                    <CustomTableCell align="right">
                      {deal.reserveCoin != null ? deal.reserveCoin : '-'}
                    </CustomTableCell>
                    <CustomTableCell align="center">
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={() => this.handleOpenDetail(deal)}
                      >
                        <Typography component="p" className={classes.text}>
                          Detail
                        </Typography>
                      </Button>
                    </CustomTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={countRows}
            rowsPerPage={limit}
            page={page - 1}
            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
            nextIconButtonProps={{ 'aria-label': 'Next Page' }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    );
  }
}

TableReservations.defaultProps = {
  classes: {},
  reservationsData: null,
  loading: false,
  refetch: () => {},
  client: null,
};

TableReservations.propTypes = {
  classes: PropTypes.object,
  reservationsData: PropTypes.object,
  loading: PropTypes.bool,
  refetch: PropTypes.func,
  client: PropTypes.object,
};

const ReservationsQuery = graphql(getReservationsByAdmin, {
  options: () => ({
    variables: {
      input: {
        page: 1,
        limit: 15,
        title: null,
        consumerName: null,
        merchantName: null,
        status: null,
        confirmedFromDate: null,
        confirmedToDate: null,
        minCashback: null,
        reservedFromDate: null,
        reservedToDate: null,
        closedFromDate: null,
        closedToDate: null,
        expiredFromDate: null,
        expiredToDate: null,
      },
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => ({
    loading: data.loading,
    reservationsData: data.getReservationsByAdmin,
    refetch: data.refetch,
  }),
});

export default withStyles(styles)(compose(ReservationsQuery, withApollo)(TableReservations));
